import { Inject } from '@odinjs/odin';

import { AutocompleteAPI, DeckAPI, DeckValue, FetchAPI, FilterAPI } from '@philips-internal/oswald-ui-components';
import { PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnClear, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { ClinicalNotesListAliases, FragmentNames } from '../../../codegen/index';
import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { ItemPepType, RulesItemConfiguration } from '../../../constants/utils-pep';
import { DateFilterUtils } from '../../../utils/date-filter-utils';
import { YesNoType } from '../../../utils/enum-utils';
import ShowToast from '../../../utils/toast-service';
import BaseController from '../../BaseController';
import { DeckContext } from '../../commons/Deck/deck-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import ClinicalNotesService from '../clinical-note-service';
import { OpenConsentModalService } from '../clinical-notes-consent/open-consent-modal-service';
import './clinical-notes-list-controller.css';

interface FilterParam {
  endDate: Date;
  startDate: Date;
}

@Controller({ component: ClinicalNotesListAliases.LAYOUT_CLINICAL_NOTES_LIST, fragment: FragmentNames.CLINICAL_NOTES_LIST })
class ClinicalNotesListController extends BaseController {

  @Inject(ClinicalNotesListAliases.FETCH_CLINICAL_NOTES_LIST)
  private fetchClinicalNotesApi!: FetchAPI;

  @Inject(ClinicalNotesListAliases.FILTER_CLINICAL_NOTES)
  private filter!: FilterAPI;

  @Inject(ClinicalNotesListAliases.AUTOC_CLINICAL_NOTES_TYPE)
  private autoCompleteNotesType!: AutocompleteAPI;

  @Inject(ClinicalNotesListAliases.DECK_CLINICAL_NOTES)
  private deckClinicalNotesApi!: DeckAPI;

  @Inject(ClinicalNotesListAliases.PANEL_GLOBAL_ACTIONS)
  private panelGlobalActionsApi!: PanelAPI;

  internalNavigation!: boolean;

  @OnClear({ target: ClinicalNotesListAliases.FILTER_CLINICAL_NOTES })
  handleFilterClear(): void {
    this.filter.clear();
    this.autoCompleteNotesType.setValue({} as Record<string, Value>)
  }

  @OnMount({ target: ClinicalNotesListAliases.FILTER_CLINICAL_NOTES })
  handleFilterMount(): void {
    const {
      lastMonth,
      currentDate
    } = DateFilterUtils.getDateRange();

    const record = {
      clinicalNotesPresentationId: {},
      endDate: currentDate,
      startDate: lastMonth,
    };

    const restored = this.filter.restoreState(true);
    const firstOptionPresentationId = 1;
    const presentationClinicalNotes = {
      description: MessageService.translate('text.exam-presentation'),
      id: firstOptionPresentationId
    };
    record.clinicalNotesPresentationId = presentationClinicalNotes;
    if (restored) {
      Object.assign(record, this.filter.getRecord());
    }
    this.filter.setData(record);
    this.filter.submit();
  }

  @OnUnmount({ target: ClinicalNotesListAliases.FILTER_CLINICAL_NOTES })
  handleFilterUnmount(e: MountEvent): void {
    const filter = e.target as FilterAPI;
    if ( this.internalNavigation ) {
      const record = filter.getRecord() as Record<string, FilterParam>;
      filter.setData(record);
      filter.persistState();
    } else {
      filter.clearPersistedState();
    }
  }

  @OnMount({ target: ClinicalNotesListAliases.LAYOUT_CLINICAL_NOTES_LIST })
  handleMount(): void {
    InformationBarUtils.collapsedBar = true;
    this.internalNavigation = false;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });

    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && ClinicalNotesService.isAllowEdit;
        this.panelGlobalActionsApi.setVisible(allowInfo);
      });
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.CLINICAL_NOTE;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        ClinicalNotesService.isAllowEdit = isAllowEdit;
      })
  }

  @OnUnmount({ target: ClinicalNotesListAliases.LAYOUT_CLINICAL_NOTES_LIST })
  handleUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnClick({ target: ClinicalNotesListAliases.ADD_CLINICAL_NOTES_CUSTOM_DYNAMIC_BUTTON })
  handleAcceptCustomDynamicButtonMount(): void {
    OpenConsentModalService.openConsentOptionsModal()
  }

  @OnClick({ target: ClinicalNotesListAliases.DECK_CLINICAL_NOTES })
  handleCardClick(event: ClickEvent): void {
    const { cdEvolucao, ieStatus, ieTranscricao } = event.value;
    this.internalNavigation = true;
    const activePendingStatus = ieStatus || 'P';
    const activeStatus = 'A';
    const isEdit = 'S';
    const allowInfo = InformationBarUtils.allowInfo && ClinicalNotesService.isAllowEdit;
    const accessViewer = (activePendingStatus === activeStatus) || (!allowInfo) || ieTranscricao === YesNoType.YES;
    const route = accessViewer ? ApplicationRoute.CLINICAL_NOTES_VIEWER : ApplicationRoute.CLINICAL_NOTES_EDIT;
    const params = accessViewer ? { cdEvolucao, eStatus: activePendingStatus } : { cdEvolucao, eStatus: activePendingStatus, isEdit };

    applicationContext.redirect(route, params);
  }

  @OnBeforeSubmit({ target: ClinicalNotesListAliases.FILTER_CLINICAL_NOTES })
  handleFilterClinicalNotesBeforeSubmit(event: SubmitEvent) : void {
    const dateLimit = 90;
    const params = {
      clinicalNotesPresentationId: event.record.clinicalNotesPresentationId,
      endDate: event.record.endDate,
      startDate: event.record.startDate
    } as FilterParam;

    if(DateFilterUtils.diffDate(event.record.endDate, event.record.startDate) > dateLimit) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }

    this.fetchClinicalNotesApi.setParameters(params as unknown as Record<string, FilterParam>);

    this.fetchClinicalNotesApi.forceFetch();
  }

  @OnAfterFetch(ClinicalNotesListAliases.FETCH_CLINICAL_NOTES_LIST)
  handleFetchCinicalNotes(event: FetchEvent): void {
    const data = event.data as Record<string, string>[];
    const deckValue = data.map(item => ({
      ...item
    })) as DeckValue[];
    DeckContext.setParamActivate(deckValue);
    this.deckClinicalNotesApi.setData(deckValue);
  }

  @OnMount({ target: ClinicalNotesListAliases.AUTOC_CLINICAL_NOTES_PRESENTATION })
  handleMountPresentation(event: MountEvent): void {
    const fieldApi = event.target as AutocompleteAPI;
    const data = [
      {
        description: MessageService.translate('text.exam-presentation'),
        id: 1,
      },
      {
        description: MessageService.translate('text.all-patient'),
        id: 2,
      }
    ]

    fieldApi.setData(data);
    fieldApi.setValue(data[0]);
  }
}

export default ClinicalNotesListController;
